/**
 * DocumentFormattedMarkdownOverlay - formatted document view for AI responses and plans.
 */

import { ListTodo } from 'lucide-react'
import { Markdown } from '../markdown'
import type { AnnotationV1 } from '@okclawpro/core'
import type { ExternalOpenAnnotationRequest } from '../annotations/use-annotation-interaction-controller'
import { FullscreenOverlayBase } from './FullscreenOverlayBase'
import { FullscreenOverlayBaseHeader, type OverlayTypeBadge } from './FullscreenOverlayBaseHeader'
import { OverlayErrorBanner } from './OverlayErrorBanner'
import { AnnotatableMarkdownDocument } from './AnnotatableMarkdownDocument'

export interface DocumentFormattedMarkdownOverlayProps {
  /** The content to display (markdown) */
  content: string
  /** Whether the overlay is open */
  isOpen: boolean
  /** Called when overlay should close */
  onClose: () => void
  /** Render inline without dialog/portal, for embedded drawer previews */
  embedded?: boolean
  /** Variant: 'response' (default) or 'plan' (shows header) */
  variant?: 'response' | 'plan'
  /** Callback for URL clicks */
  onOpenUrl?: (url: string) => void
  /** Callback for file path clicks */
  onOpenFile?: (path: string) => void
  /** Optional base path for resolving relative image/file links */
  basePath?: string
  /** Additional directories for resolving bare relative file names */
  relativeFileBasePaths?: string[]
  /** Optional file path, shown as a header badge */
  filePath?: string
  /** Optional type badge shown in header */
  typeBadge?: OverlayTypeBadge
  /** Optional error message */
  error?: string
  /** Optional session id used for annotation payload source metadata */
  sessionId?: string
  /** Optional message id; when present with callbacks, overlay becomes annotatable */
  messageId?: string
  /** Persisted annotations for the message */
  annotations?: AnnotationV1[]
  /** Callback to add annotation */
  onAddAnnotation?: (messageId: string, annotation: AnnotationV1) => void
  /** Callback to remove annotation */
  onRemoveAnnotation?: (messageId: string, annotationId: string) => void
  /** Callback to update annotation */
  onUpdateAnnotation?: (messageId: string, annotationId: string, patch: Partial<AnnotationV1>) => void
  /** Input send key behavior used by follow-up editor */
  sendMessageKey?: 'enter' | 'cmd-enter'
  /** Whether source content is currently streaming */
  isStreaming?: boolean
  /** Optional external request to open a specific annotation */
  openAnnotationRequest?: ExternalOpenAnnotationRequest | null
}

export function DocumentFormattedMarkdownOverlay({
  content,
  isOpen,
  onClose,
  embedded = false,
  variant = 'response',
  onOpenUrl,
  onOpenFile,
  basePath,
  relativeFileBasePaths,
  filePath,
  typeBadge,
  error,
  sessionId,
  messageId,
  annotations,
  onAddAnnotation,
  onRemoveAnnotation,
  onUpdateAnnotation,
  sendMessageKey = 'enter',
  isStreaming = false,
  openAnnotationRequest,
}: DocumentFormattedMarkdownOverlayProps) {
  const documentContent = (
    <div className="min-h-full flex flex-col justify-center px-6 py-16">
      <div className="bg-background rounded-[16px] shadow-strong w-full max-w-[960px] h-fit mx-auto my-auto">
        {variant === 'plan' && (
          <div className="px-4 py-2 border-b border-border/30 flex items-center gap-2 bg-success/5 rounded-t-[16px]">
            <ListTodo className="w-3 h-3 text-success" />
            <span className="text-[13px] font-medium text-success">Plan</span>
          </div>
        )}

        <div className="px-10 pt-8 pb-8">
          <div className="text-sm">
            {messageId && onAddAnnotation ? (
              <AnnotatableMarkdownDocument
                content={content}
                sessionId={sessionId}
                messageId={messageId}
                annotations={annotations}
                onAddAnnotation={onAddAnnotation}
                onRemoveAnnotation={onRemoveAnnotation}
                onUpdateAnnotation={onUpdateAnnotation}
                onOpenUrl={onOpenUrl}
                onOpenFile={onOpenFile}
                basePath={basePath}
                relativeFileBasePaths={relativeFileBasePaths}
                sendMessageKey={sendMessageKey}
                islandZIndex={420}
                openAnnotationRequest={openAnnotationRequest}
                isStreaming={isStreaming}
              />
            ) : (
              <Markdown
                mode="minimal"
                onUrlClick={onOpenUrl}
                onFileClick={onOpenFile}
                basePath={basePath}
                relativeFileBasePaths={relativeFileBasePaths}
                hideFirstMermaidExpand={false}
              >
                {content}
              </Markdown>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  if (embedded) {
    if (!isOpen) return null

    return (
      <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border border-foreground/5 bg-foreground-3">
        <FullscreenOverlayBaseHeader
          onClose={onClose}
          filePath={filePath}
          typeBadge={typeBadge}
          copyContent={content}
        />
        <div className="min-h-0 flex-1 overflow-y-auto">
          {error && (
            <div className="px-6 pt-6">
              <OverlayErrorBanner label="Write Failed" message={error} />
            </div>
          )}
          {documentContent}
        </div>
      </div>
    )
  }

  return (
    <FullscreenOverlayBase
      isOpen={isOpen}
      onClose={onClose}
      filePath={filePath}
      typeBadge={typeBadge}
      copyContent={content}
      error={error ? { label: 'Write Failed', message: error } : undefined}
    >
      {documentContent}
    </FullscreenOverlayBase>
  )
}
